import api from './api';
import { Product, ProductCategory, RepricingSuggestion, ApplyRepricingPayload } from '@/types';
import { Dimension, Unit } from '@/utils/units';

export interface ProductFilters {
  category_id?: number;
  supplier_id?: number;
  search?: string;
  only_active?: boolean;
  only_low_stock?: boolean;
  is_combo?: boolean;  // undefined=todos, true=solo combos, false=solo productos
  skip?: number;
  limit?: number;
}

// Item de combo enviado al backend (descriptivo)
export interface ComboItemPayload {
  component_product_id?: number | null;
  component_name?: string | null;
  quantity: number;
}

// Asignar/borrar el código de barras. Endpoint aparte del ABM: lo puede usar el
// empleado (permiso `inventory`), que es quien anda con la pistola en el mostrador.
export async function asignarCodigoProducto(productId: number, codigo: string | null) {
  const { data } = await api.patch(`/products/${productId}/codigo`, null, {
    params: { codigo: codigo ?? '' },
  });
  return data;
}

export interface ProductCreate {
  // null = borrar el valor. Con undefined la clave no viaja y el backend lo
  // deja como estaba, que es lo que impedía vaciar el código de barras.
  code?: string | null;
  name: string;
  description?: string | null;
  category_id?: number;
  supplier_id?: number;
  cost_price?: number;       // $ por base_unit
  freight_cost?: number | null;
  tax_cost?: number | null;
  commission_cost?: number | null;
  iva_rate?: number | null;
  sale_price?: number | null;       // EFECTIVO ($/base_unit) — null borra el precio
  pack_price?: number | null;       // precio de venta por caja (total de la caja)
  list_price?: number | null;       // TARJETA ($/base_unit)
  transfer_price?: number | null;   // TRANSFERENCIA ($/base_unit)
  qr_price?: number | null;         // QR ($/base_unit)
  current_stock?: number;
  minimum_stock?: number;
  track_stock?: boolean;
  units_per_pack?: number;
  dimension?: Dimension;
  display_unit?: Unit;
  expiration_date?: string;
  batch_number?: string;
  margin_min_override?: number | null;
  margin_target_override?: number | null;
  is_active?: boolean;
  is_combo?: boolean;
  cost_from_components?: boolean;
  combo_items?: ComboItemPayload[];
  source_product_id?: number | null;  // Fraccionado: producto padre (granel)
  fraction_grams?: number | null;     // Fraccionado: gramos por paquete vendido
}

export interface PriceUpdateBulk {
  percentage?: number;
  items?: { product_id: number; new_price: number; reason?: string }[];
  category_id?: number;
  reason?: string;
}

export const productsService = {
  // Categorías
  async getCategories(): Promise<ProductCategory[]> {
    const response = await api.get<ProductCategory[]>('/products/categories');
    return response.data;
  },

  async createCategory(data: {
    name: string;
    description?: string;
    pricing_method?: string;
    cash_multiplier?: number;
    card_multiplier?: number;
    transfer_multiplier?: number;
    qr_multiplier?: number;
    price_formula?: string;
    price_multiplier?: number;
    margin_percentage?: number;
    price_round_to?: number;
    list_price_multiplier?: number;
    margin_min?: number | null;
    margin_target?: number | null;
  }): Promise<ProductCategory> {
    const response = await api.post<ProductCategory>('/products/categories', data);
    return response.data;
  },

  async updateCategory(id: number, data: {
    name?: string;
    description?: string;
    is_active?: boolean;
    pricing_method?: string;
    cash_multiplier?: number;
    card_multiplier?: number;
    transfer_multiplier?: number;
    qr_multiplier?: number;
    price_formula?: string;
    price_multiplier?: number;
    margin_percentage?: number;
    price_round_to?: number;
    list_price_multiplier?: number;
    margin_min?: number | null;
    margin_target?: number | null;
  }): Promise<ProductCategory> {
    const response = await api.put<ProductCategory>(`/products/categories/${id}`, data);
    return response.data;
  },

  async deleteCategory(id: number): Promise<void> {
    await api.delete(`/products/categories/${id}`);
  },

  // Productos
  async getProducts(filters?: ProductFilters): Promise<Product[]> {
    const response = await api.get<Product[]>('/products', { params: filters });
    return response.data;
  },

  async getProduct(id: number): Promise<Product> {
    const response = await api.get<Product>(`/products/${id}`);
    return response.data;
  },

  async createProduct(data: ProductCreate): Promise<Product> {
    const response = await api.post<Product>('/products', data);
    return response.data;
  },

  async updateProduct(
    id: number,
    data: Partial<ProductCreate>,
    options?: {
      cost_notes?: string;
      supplier?: string;
      quantity_purchased?: number;
    }
  ): Promise<Product> {
    const response = await api.put<Product>(`/products/${id}`, data, {
      params: options,
    });
    return response.data;
  },

  async updateStock(id: number, quantity: number, quantity_unit?: Unit): Promise<Product> {
    const response = await api.patch<Product>(`/products/${id}/stock`, null, {
      params: { quantity, quantity_unit },
    });
    return response.data;
  },

  async bulkUpdatePrices(data: PriceUpdateBulk): Promise<{ message: string; updated_count: number }> {
    const response = await api.post('/products/prices/bulk-update', data);
    return response.data;
  },

  async bulkUpdatePricesByIds(data: {
    product_ids: number[];
    percentage: number;
    recalculate_from_cost?: boolean;
    reason?: string;
  }): Promise<{ updated_count: number; products_updated: number[] }> {
    const response = await api.post('/products/prices/bulk-update-by-ids', data);
    return response.data;
  },

  // Setea el stock mínimo (en la unidad de cada producto) para varios productos
  async bulkUpdateMinimumStock(data: {
    product_ids: number[];
    minimum_stock: number;
  }): Promise<{ message: string; updated_count: number }> {
    const response = await api.post('/products/minimum-stock/bulk', data);
    return response.data;
  },

  async previewBulkUpdatePricesByIds(data: {
    product_ids: number[];
    percentage: number;
    recalculate_from_cost?: boolean;
    reason?: string;
  }): Promise<{
    total_products: number;
    will_update: number;
    will_skip: number;
    items: Array<{
      product_id: number;
      product_name: string;
      code?: string;
      category_name?: string;
      old_cost?: number;
      new_cost?: number;
      old_sale_price?: number;
      new_sale_price?: number;
      old_list_price?: number;
      new_list_price?: number;
      skipped_reason?: string;
    }>;
  }> {
    const response = await api.post('/products/prices/bulk-update-by-ids/preview', data);
    return response.data;
  },

  async recalculateListPrices(categoryId?: number): Promise<{
    message: string;
    updated_count: number;
    surcharge_percent: number;
  }> {
    const response = await api.post('/products/prices/recalculate-list-prices', null, {
      params: categoryId ? { category_id: categoryId } : undefined,
    });
    return response.data;
  },

  async recalculateCategoryPrices(categoryId: number): Promise<{ updated_count: number }> {
    const response = await api.post(`/products/categories/${categoryId}/recalculate-prices`);
    return response.data;
  },

  async deleteProduct(id: number): Promise<void> {
    await api.delete(`/products/${id}`);
  },

  async uploadImage(id: number, file: File): Promise<Product> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<Product>(`/products/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async deleteImage(id: number): Promise<Product> {
    const response = await api.delete<Product>(`/products/${id}/image`);
    return response.data;
  },

  async importExcel(file: File): Promise<{ created: number; updated: number; errors: { row: number; error: string }[] }> {
    const fd = new FormData();
    fd.append('file', file);
    // Un Excel con cientos de filas puede pasarse largo del timeout global de 60s,
    // asi que esta llamada tiene el suyo, mas holgado.
    const { data } = await api.post('/products/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
    return data;
  },

  async downloadImportTemplate(): Promise<Blob> {
    const { data } = await api.get('/products/import/template', { responseType: 'blob' });
    return data;
  },

  // Repricing
  async getRepricingSuggestions(): Promise<RepricingSuggestion[]> {
    const response = await api.get<RepricingSuggestion[]>('/products/repricing-suggestions');
    return response.data;
  },

  async applyRepricing(productId: number, payload: ApplyRepricingPayload): Promise<Product> {
    const response = await api.post<Product>(`/products/${productId}/apply-repricing`, payload);
    return response.data;
  },
};
